import { apiClient } from "@/lib/apiClient";
import {
  FetchOrdersOptions,
  SearchOrdersOptions,
} from "@/types/models/order";

export const getOrders = async (options: FetchOrdersOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.limit) params.limit = options.limit.toString();
  if (options.sort_by) params.sort_by = options.sort_by;
  if (options.sort_order) params.sort_order = options.sort_order;
  if (options.status) params.status = options.status;
  if (options.created_after) params.created_after = options.created_after;
  if (options.created_before) params.created_before = options.created_before;

  return await apiClient.getPaginated("/api/v1/admin/:storeId/order", params);
};

export const searchOrders = async (options: SearchOrdersOptions) => {
  const params: Record<string, string> = {
    q: options.q,
  };

  if (options.page) params.page = options.page.toString();
  if (options.limit) params.limit = options.limit.toString();

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/order/search",
    params,
  );
};

export const getOrderDetails = async (orderId: string) => {
  return await apiClient.get(`/api/v1/admin/:storeId/order/${orderId}`);
};

export const approveOrder = async (orderId: string) => {
  return await apiClient.put(`/api/v1/admin/:storeId/order/${orderId}/approve`);
};

export const rejectOrder = async (orderId: string) => {
  return await apiClient.put(`/api/v1/admin/:storeId/order/${orderId}/reject`);
};

export const getOrderSet = async (orderId: string, setId: string) => {
  return await apiClient.get(
    `/api/v1/admin/:storeId/order/${orderId}/set/${setId}`,
  );
};

export const updateShippingDetails = async (
  orderId: string,
  setId: string,
  shippingDetails: any,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/order/${orderId}/set/${setId}/shippingDetails`,
    shippingDetails,
  );
};

export const updateOrderSetStage = async (
  orderId: string,
  setId: string,
  stage: string,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/order/${orderId}/set/${setId}/addUpdate`,
    { stage },
  );
};

export const getShipmentProvidersForOrderSet = async (
  orderId: string,
  setId: string,
) => {
  return await apiClient.get(
    `/api/v1/admin/:storeId/order/${orderId}/set/${setId}/shipment-providers`,
  );
};

// Create Manual Order - Legacy payload
export interface CreateOrderPayload {
  customer_id: string;
  customer_address: string | Record<string, string>;
  use_existing_address: boolean;
  payment_type: "PREPAID" | "POSTPAID" | "SEMIPREPAID";
  products: Array<{
    variant_id: string;
    product_id: string;
    quantity: number;
  }>;
  discount_codes?: string[];
  delivery_date?: string;
  shipping_date?: string;
  manifest_file?: File;
  awb_file?: File;
}

// Enhanced Order Creation payload
export interface AdditionalCharges {
  shipping_charge: number;
  delivery_charge: number;
  gift_wrap_charge: number;
  transaction_charge: number;
  total_discount: number;
  discount_type: "percentage" | "fixed";
}

export interface PackageDetails {
  weight: number;
  weight_unit: "g" | "kg";
  length: number;
  breadth: number;
  height: number;
}

export interface ShippingProvider {
  id: string;
  partnerName: string;
  rating: number;
  deliveryCharge: number;
  shipmentType: "AIR" | "SURFACE";
  rtoCost: number;
  expectedPickup: string;
  expectedDelivery: string;
  chargeableWeight: number;
  courierOptionId?: string;
  correlationId?: string;
}

export interface TempPickupAddress {
  name: string;
  contact_name: string;
  phone_number: string;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  postal_code: string;
}

export interface NewCustomer {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

export interface EnhancedCreateOrderPayload {
  pickup_address_id: string;
  use_temp_pickup: boolean;
  temp_pickup_address?: TempPickupAddress;
  customer_id: string;
  is_new_customer: boolean;
  new_customer?: NewCustomer;
  customer_address: string | Record<string, string>;
  use_existing_address: boolean;
  payment_type: "PREPAID" | "POSTPAID" | "SEMIPREPAID";
  semi_prepaid_amount?: number;
  products: Array<{
    variant_id: string;
    product_id: string;
    quantity: number;
    unit_price?: number;
    discount?: number;
    tax_rate?: number;
    hsn_code?: string;
    is_temporary?: boolean;
    temp_name?: string;
    temp_sku?: string;
  }>;
  additional_charges: AdditionalCharges;
  package_details: PackageDetails;
  shipping_type: "shiphouse" | "manual";
  shipping_provider?: ShippingProvider;
  manual_shipping?: {
    awb_code: string;
    courier_name: string;
    tracking_url?: string;
  };
  manifest_file?: File;
  awb_file?: File;
}

export const createOrder = async (payload: CreateOrderPayload) => {
  const formData = new FormData();

  // Add required fields
  formData.append("customer_id", payload.customer_id);
  formData.append("payment_type", payload.payment_type);
  formData.append("products", JSON.stringify(payload.products));
  formData.append("use_existing_address", String(payload.use_existing_address));

  // Add customer address
  if (typeof payload.customer_address === "string") {
    formData.append("customer_address", payload.customer_address);
  } else {
    formData.append(
      "customer_address",
      JSON.stringify(payload.customer_address),
    );
  }

  // Add optional fields
  if (payload.discount_codes && payload.discount_codes.length > 0) {
    formData.append("discount_codes", JSON.stringify(payload.discount_codes));
  }

  if (payload.delivery_date) {
    formData.append("delivery_date", payload.delivery_date);
  }

  if (payload.shipping_date) {
    formData.append("shipping_date", payload.shipping_date);
  }

  // Add files if present
  if (payload.manifest_file) {
    formData.append("manifest_file", payload.manifest_file);
  }

  if (payload.awb_file) {
    formData.append("awb_file", payload.awb_file);
  }

  return await apiClient.post("/api/v1/admin/:storeId/order/create", formData);
};

// Enhanced order creation with all details
export const createManualOrder = async (
  payload: EnhancedCreateOrderPayload,
) => {
  const formData = new FormData();

  // Pickup
  formData.append("pickup_address_id", payload.pickup_address_id || "");
  formData.append("use_temp_pickup", String(payload.use_temp_pickup));
  if (payload.use_temp_pickup && payload.temp_pickup_address) {
    formData.append(
      "temp_pickup_address",
      JSON.stringify(payload.temp_pickup_address),
    );
  }

  // Customer
  formData.append("customer_id", payload.customer_id || "");
  formData.append("is_new_customer", String(payload.is_new_customer));
  if (payload.is_new_customer && payload.new_customer) {
    formData.append("new_customer", JSON.stringify(payload.new_customer));
  }

  // Address
  formData.append("use_existing_address", String(payload.use_existing_address));
  if (typeof payload.customer_address === "string") {
    formData.append("customer_address", payload.customer_address);
  } else {
    formData.append(
      "customer_address",
      JSON.stringify(payload.customer_address),
    );
  }

  // Payment
  formData.append("payment_type", payload.payment_type);
  if (payload.semi_prepaid_amount !== undefined) {
    formData.append("semi_prepaid_amount", String(payload.semi_prepaid_amount));
  }

  // Products
  formData.append("products", JSON.stringify(payload.products));

  // Charges
  formData.append(
    "additional_charges",
    JSON.stringify(payload.additional_charges),
  );

  // Package (convert weight to grams)
  const normalizedPackage = {
    ...payload.package_details,
    weight:
      payload.package_details.weight_unit === "kg"
        ? payload.package_details.weight * 1000
        : payload.package_details.weight,
    weight_unit: "g",
  };
  formData.append("package_details", JSON.stringify(normalizedPackage));

  // Shipping
  formData.append("shipping_type", payload.shipping_type);
  if (payload.shipping_provider) {
    const providerPayload = {
      id: payload.shipping_provider.id,
      partner_name: payload.shipping_provider.partnerName,
      delivery_charge: payload.shipping_provider.deliveryCharge,
      shipment_type: payload.shipping_provider.shipmentType,
      rto_cost: payload.shipping_provider.rtoCost,
      expected_pickup: payload.shipping_provider.expectedPickup,
      expected_delivery: payload.shipping_provider.expectedDelivery,
      chargeable_weight: payload.shipping_provider.chargeableWeight,
    };
    formData.append("shipping_provider", JSON.stringify(providerPayload));
  }

  if (payload.manual_shipping) {
    formData.append("manual_shipping", JSON.stringify(payload.manual_shipping));
  }

  // Optional files
  if (payload.manifest_file) {
    formData.append("manifest_file", payload.manifest_file);
  }
  if (payload.awb_file) {
    formData.append("awb_file", payload.awb_file);
  }

  return await apiClient.post("/api/v1/admin/:storeId/order/create", formData);
};
